import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Avatar, Button, Empty, Input, Row, Select, Card, Col } from "antd";
import NewBooking from "./components/NewBooking";
import RaiseSwapRequest from "./components/RaiseSwapRequest";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  checkPermission,
  resourceTypes,
  showNotification,
} from "../utils/CommonFunctions";
import resourceSvg from "../assets/svg/resource.svg";
import "./components/calendar.scss";
import { useHistory, useLocation } from "react-router-dom";
import { getAllBookingResources } from "./duck/BookingActions";
import Routes from "../config/Routes";
import { bookingColumns } from "./components/BookingColumns";
// import GoogleCalendar from "./components/GoogleCalendar";
import MyPagination from "../utils/MyPagination";
import MTable from "../utils/MTable";
import appUrl from "../config/AppUrl";
import BookingHistory from "./components/BookingHistory";
import routes from "../config/Routes";
import {
  CalendarOutlined,
  LeftOutlined,
  PieChartOutlined,
  PoundOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import MeetingRooms from "../resources/MeetingReports";
import AppUrl from "../config/AppUrl";
import axios from "axios";
import BarChartReact from "../resources/components/VerticalChart";

const FILTER_OPTIONS = [
  {
    key: "week",
    label: "Last Week",
    total_booking_value: "",
    average_occupancy_value: "",
  },
  {
    key: "lastMonth",
    label: "Last Month",
    total_booking_value: "",
    average_occupancy_value: "",
  },
  {
    key: "last3Months",
    label: "Last 3 Months",
    total_booking_value: "",
    average_occupancy_value: "",
  },
];

const BOOKER_RANGE_KEYS = ["week", "month", "threeMonths"];

const BOOKER_RANGE_LABELS = {
  week: "Last Week",
  month: "Last Month",
  threeMonths: "Last 3 Months",
};

const initialBookerRanges = {
  week: { topBookers: [], searchResults: [] },
  month: { topBookers: [], searchResults: [] },
  threeMonths: { topBookers: [], searchResults: [] },
};

const initialBookerChartConfig = {
  meta: {
    xLabel: "Bookers",
    yLabel: "Bookings",
    datasetLabel: "Booking Count",
    minData: 0,
    maxData: 10,
    stepSize: 1,
  },
  ranges: {
    week: { labels: [], values: [] },
    month: { labels: [], values: [] },
    threeMonths: { labels: [], values: [] },
  },
};

const getBookerName = (booker) =>
  booker?.name ||
  booker?.full_name ||
  booker?.user_name ||
  booker?.member_name ||
  booker?.member?.name ||
  booker?.user?.name ||
  "Unknown";

const getBookerEmail = (booker) =>
  booker?.email ||
  booker?.member_email ||
  booker?.user_email ||
  booker?.member?.email ||
  booker?.user?.email ||
  "";

const getBookerCount = (booker) =>
  booker?.booking_count ||
  booker?.bookingCount ||
  booker?.total_bookings ||
  booker?.totalBookings ||
  booker?.count ||
  0;

const splitChartLabel = (input, chunkSize = 2) => {
  if (typeof input !== "string") return [];

  const words = input.split(" ");
  const result = [];

  for (let i = 0; i < words.length; i += chunkSize) {
    result.push(words.slice(i, i + chunkSize).join(" "));
  }

  return result;
};

const normalizeBookersResponse = (payload) => {
  const data = payload?.data ?? payload;

  const getRange = (rangeKey) => ({
    topBookers: Array.isArray(data?.[rangeKey]?.topBookers)
      ? data[rangeKey].topBookers
      : [],
    searchResults: Array.isArray(data?.[rangeKey]?.searchResults)
      ? data[rangeKey].searchResults
      : [],
  });

  return {
    week: getRange("week"),
    month: getRange("month"),
    threeMonths: getRange("threeMonths"),
  };
};

const buildBookerChartConfig = (ranges) => {
  const values = BOOKER_RANGE_KEYS.flatMap((rangeKey) =>
    ranges[rangeKey].topBookers.map((booker) => getBookerCount(booker))
  );
  const maxValue = Math.max(10, ...values);
  const maxData = Math.ceil(maxValue / 5) * 5;

  return {
    ...initialBookerChartConfig,
    meta: {
      ...initialBookerChartConfig.meta,
      maxData,
      stepSize: Math.max(1, Math.ceil(maxData / 5)),
    },
    ranges: BOOKER_RANGE_KEYS.reduce((acc, rangeKey) => {
      acc[rangeKey] = {
        labels: ranges[rangeKey].topBookers.map((booker) =>
          splitChartLabel(getBookerName(booker))
        ),
        values: ranges[rangeKey].topBookers.map((booker) =>
          getBookerCount(booker)
        ),
      };
      return acc;
    }, {}),
  };
};

const MeetingReportDetails = () => {
  const STATUS_TYPE = {
    upcoming: {
      _id: "upcoming",
      name: "Upcoming",
    },
    ongoing: {
      _id: "ongoing",
      name: "Ongoing",
    },
    past: {
      _id: "past",
      name: "Past",
    },
    canceled: {
      _id: "canceled",
      name: "Canceled",
    },
  };
  Object.freeze(STATUS_TYPE);

  const PAYMENT_STATUS_TYPE = Object.freeze([
    {
      _id: "completed",
      name: "Completed",
    },
    {
      _id: "pending",
      name: "Pending",
    },
    {
      _id: "failed",
      name: "Failed",
    },
  ]);

  const preProps = useRef();

  const isBookingEditable = useMemo(
    () =>
      checkPermission({
        row: "booking",
        subRow: "booking",
      }),
    []
  );
  const calendarShow = useMemo(
    () =>
      checkPermission({
        row: "booking",
        subRow: "calendar",
        type: "show",
      }),
    []
  );

  const showAllBooking = useMemo(
    () => checkPermission({ initial: "showAllBooking" }),
    []
  );
  const showAllWorkSpaces = useMemo(
    () => checkPermission({ initial: "showAllWorkSpaces" }),
    []
  );
  const { search } = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const getValueFromUrl = new URLSearchParams(search);

  const [showSwapRequest, setShowSwapRequest] = useState(false);
  const [showAddNewBooking, setShowAddNewBooking] = useState(false);
  const [showBookingHistory, setShowBookingHistory] = useState(false);
  const [selectedBooking, selectBooking] = useState(null);
  const [bookingStatus, setBookingStatus] = useState("new");
  const [filter, setFilter] = useState({});
  const [boardKey, setBoardKey] = useState();
  const [startIndex, setStartIndex] = useState(0);
  const [activeFilter, setActiveFilter] = useState("");
  const [chartData, setChartData] = useState(null);
  const [bookerRange, setBookerRange] = useState("week");
  const [bookerSearchText, setBookerSearchText] = useState("");
  const [bookerSearch, setBookerSearch] = useState("");
  const [frequentBookers, setFrequentBookers] = useState(initialBookerRanges);
  const [bookerChartConfig, setBookerChartConfig] = useState(
    initialBookerChartConfig
  );
  const [frequentBookersLoading, setFrequentBookersLoading] = useState(false);

  const workSpace = useSelector((state) => state?.workSpace);
  const activeWorkSpace = workSpace?.active;
  const booking = useSelector((state) => state?.booking);
  const mBookingBoard = useMemo(
    () => booking.bookings?.[boardKey] ?? {},
    [booking.bookings, boardKey]
  );

  const searchParams = new URLSearchParams(search);
  const resourceId = searchParams.get("resourceId");
  //  console.log(chartData, resourceId, "resourceId");

  const getChartData = useCallback(async () => {
    try {
      const response = await axios.get(
        AppUrl.REPORT_CHART_DETAILS + "/" + resourceId + "/report"
      );

      if (response.data?.meta?.success) {
        const apiData = response.data.data;

        const list = Object.entries(apiData).map(([key, value]) => ({
          key,
          label:
            key === "week"
              ? "Last Week"
              : key === "month"
              ? "Last Month"
              : "Last 3 Months",
          totalBookings: value.total_bookings,
          avgOccupancy: Math.round(value.avgOccupancy),
        }));
        console.log(list, "list");
        setChartData(list);
        setActiveFilter(list[0]); // ✅ FULL OBJECT
      }
    } catch (error) {
      console.error("Server error:", error.message);
    }
  }, [resourceId]);

  useEffect(() => {
    getChartData();
  }, [getChartData]);

  const getFrequentBookers = useCallback(async () => {
    if (!resourceId) return;

    try {
      setFrequentBookersLoading(true);
      const response = await axios.get(
        `${AppUrl.REPORT_CHART_DETAILS}/${resourceId}/frequent-bookers`,
        {
          params: {
            q: bookerSearch || undefined,
            limit: 10,
          },
        }
      );

      if (response.data?.meta?.success !== false) {
        const bookerRanges = normalizeBookersResponse(response.data);
        setFrequentBookers(bookerRanges);
        setBookerChartConfig(buildBookerChartConfig(bookerRanges));
      }
    } catch (error) {
      console.error("Frequent bookers error:", error.message);
      setFrequentBookers(initialBookerRanges);
      setBookerChartConfig(initialBookerChartConfig);
    } finally {
      setFrequentBookersLoading(false);
    }
  }, [bookerSearch, resourceId]);

  useEffect(() => {
    getFrequentBookers();
  }, [getFrequentBookers]);

  const onBookerSearch = useCallback(() => {
    setBookerSearch(bookerSearchText.trim());
  }, [bookerSearchText]);

  const onBookerReset = useCallback(() => {
    setBookerSearchText("");
    setBookerSearch("");
  }, []);

  const activeBookerRange = bookerRange || "week";
  const activeSearchResults =
    frequentBookers?.[activeBookerRange]?.searchResults || [];

  const renderBookerCards = useCallback((bookers) => {
    if (!bookers.length) {
      return <Empty description="No bookers found" />;
    }

    return (
      <Row gutter={[12, 12]}>
        {bookers.map((booker, index) => {
          const name = getBookerName(booker);
          const email = getBookerEmail(booker);
          const count = getBookerCount(booker);
          const key =
            booker?._id ||
            booker?.id ||
            booker?.user_id ||
            booker?.member_id ||
            email ||
            index;

          return (
            <Col xs={24} key={key}>
              <div
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 4,
                  padding: 14,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  minHeight: 78,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: 0,
                  }}
                >
                  <Avatar
                    icon={<UserOutlined />}
                    style={{
                      marginRight: 12,
                      backgroundColor: "#7367f0",
                      flex: "0 0 auto",
                    }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={name}
                    >
                      {name}
                    </div>
                    <small
                      style={{
                        color: "#6e6b7b",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                      title={email}
                    >
                      {email || "-"}
                    </small>
                  </div>
                </div>
                <div
                  style={{
                    textAlign: "right",
                    marginLeft: 12,
                    flex: "0 0 auto",
                  }}
                >
                  <div style={{ fontSize: 18, fontWeight: 600 }}>{count}</div>
                  <small style={{ color: "#6e6b7b" }}>bookings</small>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    );
  }, []);

  useEffect(() => {
    if (!resourceId || resourceId.trim() === "") {
      history.push(Routes.meetingReports);
    }
  }, [resourceId, history]);

  let isWorkSpaceActive = workSpace?.workSpaceMap?.[activeWorkSpace]?.active;

  const loadMore = useCallback(
    (offset = 0, limit = 30, o) => {
      const { meta, list, loading } = mBookingBoard;

      if (!loading && (offset === 0 || meta?.totalCount > list?.length)) {
        const { resource_type, booking_status, payment_status } = filter;

        let params = {};
        if (showAllBooking && activeWorkSpace)
          params.space_id = activeWorkSpace;
        if (resource_type) params.resource_type = resource_type;
        if (booking_status) params.booking_status = booking_status;
        if (payment_status) params.payment_status = payment_status;
        if (!o && getValueFromUrl.get("resourceId"))
          params.resource_id = getValueFromUrl.get("resourceId");
        if (!o && getValueFromUrl.get("from"))
          params.from = getValueFromUrl.get("from");
        if (!o && getValueFromUrl.get("to"))
          params.to = getValueFromUrl.get("to");
        setBoardKey(JSON.stringify({ ...params }));
        params.offset = offset;
        params.limit = limit;
        dispatch(getAllBookingResources(params));
      }
    },
    [
      activeWorkSpace,
      mBookingBoard,
      filter,
      showAllBooking,
      dispatch,
      getValueFromUrl,
    ]
  );

  const handleTableChange = useCallback(
    (pagination) => {
      const { current, pageSize } = pagination;
      setStartIndex((current - 1) * pageSize);
      const { list } = mBookingBoard;
      const endIndex = current * pageSize;

      if (endIndex > list?.length) {
        loadMore(list?.length, endIndex - list?.length);
      }
    },
    [mBookingBoard, loadMore]
  );

  const onSearch = () => loadMore();
  /* const onReset = useCallback((o) => {
    if (o) {
      setFilter({});
      loadMore(0, 30, o);
      history.push(routes.booking);
    } else {
      setFilter({ resource_type: getValueFromUrl.get("resourceType") });
    }
    setBoardKey("");
    setStartIndex(0); 
  }, []); */

  const onReset = useCallback((o) => {
    setFilter((prev) => ({
      ...prev,
      booking_status: "",
      payment_status: "",
    }));
    loadMore();
  }, []);

  useEffect(() => {
    if (preProps?.current?.activeWorkSpace !== activeWorkSpace) {
      onReset();
    } else if (!Object.keys(mBookingBoard).length) {
      loadMore();
    }
    return () => {
      preProps.current = { activeWorkSpace };
    };
  }, [activeWorkSpace, mBookingBoard, loadMore]);

  const handleChange = useCallback(
    (name) => (event) => {
      let value = event?.target?.value ?? event;

      setFilter((pre) => ({
        ...pre,
        [name]: value,
      }));
    },
    []
  );

  const handleShowNewBooking = (show = false) => {
    show = typeof show === "boolean" && show;
    setShowAddNewBooking(show);
    if (!show) selectBooking(null);
  };

  const handleShowSwapRequest = (show = false, booking = null) => {
    show = typeof show === "boolean" && show;

    selectBooking(booking);
    setShowSwapRequest(show);
  };

  const updateMeeting = (e) => {
    const today = moment();
    let status = "";
    if (today.isBetween(moment(e?.from), moment(e?.to))) {
      status = "ongoing";
      // showNotification('warning', 'You cannot update an ongoing event')
    } else if (moment(e?.from).isBefore(today, "time")) {
      status = "past";
      // showNotification('warning', 'You cannot update an event from the past')
    }
    // else if( moment(e?.from).isAfter(today, 'time')){
    // }

    setBookingStatus(status);
    selectBooking(e);
    handleShowNewBooking(true);
  };

  const handleData = (option) => {
    setActiveFilter(option);
  };

  const bookings = mBookingBoard?.list;

  let data = useMemo(() => {
    let mData = [];
    if (bookings) {
      bookings.forEach((id, index) => {
        const mBooking = booking?.bookingMap?.[id];
        const startDateTime = new Date(mBooking?.from).getTime();
        const endDateTime = new Date(mBooking?.to).getTime();
        const diffInTime = (endDateTime - startDateTime) / (1000 * 3600);
        const area = mBooking?.space_id?.area;
        const name = mBooking?.space_id?.name;
        const today = moment();
        let status = "Upcoming";

        if (!mBooking?.active) {
          status = "Canceled";
        } else if (mBooking?.payment_status === "failed") {
          status = "Failed";
        } else if (mBooking?.payment_status !== "completed") {
          status = "Payment Pending";
        } else if (
          today.isBetween(moment(mBooking?.from), moment(mBooking?.to))
        ) {
          status = "Ongoing";
        } else if (moment(mBooking?.from).isBefore(today, "time")) {
          status = "Past";
        }

        let resource = [];
        resource = [
          mBooking?.items?.map?.((item) => item?.seat_no).join?.() ||
            mBooking?.resource_id?.name,
          mBooking?.resource_id?.color,
          mBooking?.title || resourceTypes?.[mBooking?.type]?.name,
        ];

        mData.push({
          key: id,
          serial: ++index,
          resource,
          date: [mBooking?.from, mBooking?.to],
          price: [mBooking?.price?.total || mBooking?.price?.net, diffInTime],
          members: mBooking?.members,
          action: mBooking,
          status,
          area,
          name,
        });
      });
    }

    return mData;
  }, [bookings, booking?.bookingMap]);

  return (
    <div>
      <Row align="middle" className="mb20">
        <div className="">
          <span
            className="nw_heading"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "3px",
              cursor: "pointer",
              marginBottom: "25px",
            }}
          >
            <LeftOutlined style={{ fontSize: "12px" }} />
            <i
              style={{ fontStyle: "normal", fontSize: "14px" }}
              onClick={() => history.push(Routes.meetingReports)}
            >
              Go Back
            </i>
          </span>
        </div>
        <Col span={24}>
          <Row>
            <Col span={24}>
              <Row align="middle" justify="space-between" className="mb15">
                <div className="d_title_cb">
                  {data.length > 1 && data[0].resource[0]}
                </div>

                <div className="container_btn">
                  {chartData &&
                    chartData.map((option) => (
                      <button
                        key={option.key}
                        className={
                          activeFilter.key === option.key ? "active_btn" : ""
                        }
                        onClick={() => handleData(option)}
                      >
                        {option.label}
                      </button>
                    ))}
                </div>
              </Row>
            </Col>

            <Col span={24}>
              <Row>
                {/* Total Booking */}
                <Col span={8}>
                  <Card
                    bordered={false}
                    style={{
                      width: "95%",
                      margin: "0 20px 10px 0",
                      borderRadius: 4,
                    }}
                    hoverable
                  >
                    <Row justify="space-between" align="middle">
                      <span>
                        <div style={{ fontSize: 18, fontWeight: 500 }}>
                          Total Booking
                        </div>
                        <div
                          style={{
                            fontSize: 18,
                            fontWeight: 500,
                            color: "#28c76f",
                          }}
                        >
                          {activeFilter.totalBookings}
                        </div>
                      </span>
                      <span>
                        <div
                          className="--avatar green circle"
                          style={{ height: 50, width: 50, fontSize: 20 }}
                        >
                          <CalendarOutlined />
                        </div>
                      </span>
                    </Row>
                  </Card>
                </Col>

                {/* Average Occupancy */}
                <Col span={8}>
                  <Card
                    bordered={false}
                    style={{
                      width: "95%",
                      margin: "0 20px 10px 0",
                      borderRadius: 4,
                    }}
                    hoverable
                  >
                    <Row justify="space-between" align="middle">
                      <span>
                        <div style={{ fontSize: 18, fontWeight: 500 }}>
                          Average Occupancy
                        </div>
                        <div
                          style={{
                            fontSize: 18,
                            fontWeight: 500,
                            color: "#7367f0",
                          }}
                        >
                          {activeFilter.avgOccupancy}
                        </div>
                      </span>
                      <span>
                        <div
                          className="--avatar purple circle"
                          style={{ height: 50, width: 50, fontSize: 20 }}
                        >
                          <PieChartOutlined />
                        </div>
                      </span>
                    </Row>
                  </Card>
                </Col>
              </Row>
            </Col>

            <Col span={24}>
              <Row className="container_graph_outer_cb">
                <Col className="container_graph" xs={24} lg={14}>
                  <div className="container_graph_inner f_graph">
                    <div
                      className="title_cb"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                      }}
                    >
                      <span>Most Frequent Bookers</span>
                      <div
                        className="container_btn"
                        style={{
                          marginBottom: 0,
                          flexWrap: "wrap",
                        }}
                      >
                        {chartData &&
                          chartData.map((option) => (
                            <button
                              key={option.key}
                              className={
                                bookerRange === option.key
                                  ? "active_btn"
                                  : ""
                              }
                              onClick={() => setBookerRange(option.key)}
                            >
                              {option.label}
                            </button>
                          ))}
                      </div>
                    </div>
                    {frequentBookersLoading ? (
                      <div style={{ padding: "15px 0" }}>
                        Loading bookers...
                      </div>
                    ) : (
                      <BarChartReact
                        range={activeBookerRange}
                        chartConfig={bookerChartConfig}
                      />
                    )}
                  </div>
                </Col>

                <Col className="container_graph" xs={24} lg={10}>
                  <div className="container_graph_inner f_graph">
                    <div className="title_cb">Search Bookers</div>
                    <Row align="middle" className="mb15" gutter={[8, 8]}>
                      <Col span={24}>
                        <Input
                          size="large"
                          className="custom-input"
                          style={{ width: "100%", fontWeight: "500" }}
                          placeholder="Search by name or email"
                          prefix={<SearchOutlined />}
                          value={bookerSearchText}
                          onChange={(event) =>
                            setBookerSearchText(event.target.value)
                          }
                          onPressEnter={onBookerSearch}
                          allowClear
                        />
                      </Col>
                      <Col>
                        <Button
                          className="nw_btn_black"
                          size="large"
                          type="primary"
                          onClick={onBookerSearch}
                        >
                          Search
                        </Button>
                      </Col>
                      <Col>
                        <Button
                          className="nw_btn_white"
                          size="large"
                          type="primary-outline"
                          onClick={onBookerReset}
                        >
                          Reset
                        </Button>
                      </Col>
                    </Row>

                    {bookerSearch && (
                      <div>
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 600,
                            marginBottom: 12,
                          }}
                        >
                          Search Results -{" "}
                          {BOOKER_RANGE_LABELS[activeBookerRange]}
                        </div>
                        {renderBookerCards(activeSearchResults)}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>

        {/* <span className="nw_mt_spcer">
          
          {calendarShow && (
            <Button
              className="mr5 nw_btn_common nw_btn_view"
              onClick={() => {
                history.push(Routes.calendar);
              }}
            >
              Calendar View
            </Button>
          )}
          {isBookingEditable && (
            <Button
              type="primary-outline"
              className="nw_btn_common"
              onClick={() => {
                if (showAllBooking) {
                  if (!isWorkSpaceActive) {
                    showNotification(
                      "error",
                      "You can't create a new booking because this workspace is inactive"
                    );
                  } else {
                    setBookingStatus("new");
                    handleShowNewBooking(true);
                  }
                } else {
                  // history.push(`${Routes.search}?spaceId=${activeWorkSpace}`);
                  window.location = `${appUrl.WEBSITE_URL}${Routes.homeSearch}?spaceId=${activeWorkSpace}`;
                }
              }}
            >
              + New Booking
            </Button>
          )}
        </span> */}
      </Row>

      {!bookings && bookings?.length === 0 && (
        <Row justify="center" align="middle">
          <img src={resourceSvg} alt="" />
        </Row>
      )}

      <Card
        title="Filter"
        className="nw_filter_container"
        size="small"
        bordered={false}
      >
        {/* <Select
          className="mr5 nw_mobile_container"
          style={{
            paddingTop: "5px",
            width: "15%",
            fontWeight: "500",
            marginRight: "15px",
          }}
          placeholder={"Resource Type"}
          value={filter?.resource_type}
          onChange={handleChange("resource_type")}
          allowClear
          size="large"
        >
          {Object.values(resourceTypes).map((resourceType) => (
            <Select.Option key={resourceType?._id} value={resourceType?._id}>
              {resourceType?.name}
            </Select.Option>
          ))}
        </Select> */}

        <Select
          className="nw_mobile_container"
          style={{
            width: "15%",
            fontWeight: "500",
            marginRight: "15px",
          }}
          placeholder={"Booking Status"}
          value={filter?.booking_status}
          onChange={handleChange("booking_status")}
          allowClear
          size="large"
        >
          {Object.values(STATUS_TYPE).map((statusType) => (
            <Select.Option key={statusType?._id} value={statusType?._id}>
              {statusType?.name}
            </Select.Option>
          ))}
        </Select>
        <Select
          className="nw_mobile_container"
          style={{
            width: "15%",
            fontWeight: "500",
            marginRight: "15px",
          }}
          placeholder={"Payment Status"}
          value={filter?.payment_status}
          onChange={handleChange("payment_status")}
          allowClear
          size="large"
        >
          {PAYMENT_STATUS_TYPE.map(({ _id, name }) => (
            <Select.Option key={_id} value={_id}>
              {name}
            </Select.Option>
          ))}
        </Select>

        <Button
          className="mr10 nw_btn_black"
          size="large"
          type="primary"
          onClick={onSearch}
        >
          Search
        </Button>

        <Button
          className="mr10 nw_btn_white"
          size="large"
          type="primary-outline"
          /* onClick={() => onReset("reset")} */
          onClick={onReset}
        >
          Reset
        </Button>
      </Card>

      <Row>
        <MTable
          columns={bookingColumns(
            isBookingEditable,
            updateMeeting,
            handleShowSwapRequest
          )}
          dataSource={data.slice(startIndex, startIndex + 10)}
          className="mt20 fw nw_table_container_box"
          loading={mBookingBoard?.loading}
          onChange={handleTableChange}
        />
        <Col span={24}>
          <MyPagination
            totalCount={mBookingBoard?.meta?.totalCount}
            onChangePage={handleTableChange}
          />
        </Col>
      </Row>

      <RaiseSwapRequest
        visible={showSwapRequest}
        handleModal={handleShowSwapRequest}
        selectedBookingId={selectedBooking?._id}
      />

      <NewBooking
        visible={showAddNewBooking}
        bookingId={selectedBooking?._id}
        handleModal={handleShowNewBooking}
        selectedBooking={selectedBooking}
        selectBooking={selectBooking}
        bookingStatus={bookingStatus}
        editable={isBookingEditable}
        onShowBookingHistory={() => setShowBookingHistory(true)}
      />

      <BookingHistory
        bookingId={selectedBooking?._id}
        isOpen={showBookingHistory}
        onClose={() => setShowBookingHistory(false)}
      />
    </div>
  );
};

export default MeetingReportDetails;
